import { watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/composables/useAuth'
import { beginSessionExpiry, endSessionExpiry } from '@/services/sessionExpiryGuard'

let expirationTimer: number | null = null
let initialized = false

const clearTimer = () => {
  if (expirationTimer !== null) {
    window.clearTimeout(expirationTimer)
    expirationTimer = null
  }
}

const handleSessionExpiration = () => {
  // Shared latch: if the 401 interceptor or the route guard has already
  // taken ownership of the expiry flow, bail out - no duplicate work.
  if (!beginSessionExpiry()) {
    return
  }
  // Silent expiry: clear storage and redirect to /signin, no toast.
  useAuth().logout().finally(() => endSessionExpiry())
}

const scheduleExpirationCheck = (expiresAt: number | null) => {
  clearTimer()

  if (!expiresAt) {
    return
  }

  const timeUntilExpiration = expiresAt - Date.now()

  if (timeUntilExpiration <= 0) {
    handleSessionExpiration()
    return
  }

  // Clamp delay to 2^31-1 ms - the maximum value setTimeout supports.
  // Larger values overflow to a 32-bit signed int, causing the callback
  // to fire immediately (browser spec behaviour).
  const MAX_TIMEOUT_MS = 2_147_483_647
  expirationTimer = window.setTimeout(() => {
    handleSessionExpiration()
  }, Math.min(timeUntilExpiration, MAX_TIMEOUT_MS))
}

export const initializeTokenExpirationWatcher = () => {
  if (initialized) {
    return
  }

  initialized = true

  const authStore = useAuthStore()

  watch(
    () => authStore.tokenExpiresAt,
    (expiresAt) => {
      scheduleExpirationCheck(expiresAt)
    },
    {
      immediate: true,
    },
  )
}

